import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiFileText, FiAlertCircle } = FiIcons;

const TermsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-slate-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-black uppercase tracking-[0.3em] mb-6"
          >
            <SafeIcon icon={FiShield} className="mr-2" /> Legal Agreement
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-black text-slate-900 tracking-tighter mb-6"
          >
            Terms and <span className="text-indigo-600">Conditions</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-500 font-medium max-w-2xl mx-auto"
          >
            Please read these terms carefully before using the SOHLocal platform.
          </motion.p>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-[3rem] shadow-2xl shadow-slate-200/50 p-8 md:p-16 border border-slate-100"
        >
          <div className="prose prose-slate max-w-none prose-headings:font-black prose-headings:tracking-tight prose-h2:text-2xl prose-h2:text-slate-900 prose-h2:mt-12 prose-h2:mb-6 prose-p:text-slate-600 prose-p:leading-relaxed prose-li:text-slate-600">
            <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-12 pb-8 border-b border-slate-100">
              Last Updated: May 2024
            </p>

            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-6 mb-12 flex items-start">
              <SafeIcon icon={FiFileText} className="text-indigo-600 text-2xl mr-4 flex-shrink-0 mt-1" />
              <p className="text-indigo-900 font-medium text-sm m-0">
                <strong>Welcome to SOHLocal!</strong> By accessing or using our platform, you agree to be bound by these Terms and Conditions. If you do not agree to all the terms and conditions, then you may not access the website or use any services.
              </p>
            </div>

            <h2>1. Nature of the Platform</h2>
            <p>
              SOHLocal ("we," "our," or "the platform") operates as an online marketplace connecting local food buyers ("Buyers") with independent home cooks, bakers, and small food vendors ("Sellers").
            </p>
            <p>
              <strong>SOHLocal is a venue only.</strong> We do not prepare, handle, store, inspect, deliver, or sell any food items. We are not a restaurant, bakery, or food delivery service. SOHLocal does not process direct financial transactions for the sale of goods. All payments, fulfillment, and delivery arrangements are handled entirely and directly between the Buyer and the Seller.
            </p>

            <h2>2. User Accounts and Eligibility</h2>
            <p>
              To use certain features of the platform, you must register for an account. By registering, you represent and warrant that:
            </p>
            <ul>
              <li>You are at least 18 years of age (or the age of legal majority in your jurisdiction).</li>
              <li>You will provide accurate, current, and complete registration information.</li>
              <li>You will maintain the security of your password and accept all risks of unauthorized access to your account.</li>
              <li>You will promptly notify SOHLocal if you discover or suspect any security breaches related to your account.</li>
            </ul>

            <h2>3. Seller Obligations and Food Safety</h2>
            <p>
              If you register as a Seller on SOHLocal, you explicitly agree to the following additional terms:
            </p>
            <ul>
              <li><strong>Legal Compliance:</strong> You are solely responsible for understanding and complying with all local, state, provincial, and federal laws regarding the preparation, sale, and distribution of food. This includes, but is not limited to, Cottage Food Laws, health department permits, business licenses, and tax regulations.</li>
              <li><strong>Food Safety:</strong> You must maintain strict hygiene and food safety standards. You are entirely responsible for the quality, safety, and legality of the items you list.</li>
              <li><strong>Allergen Disclosure:</strong> You must accurately and clearly disclose all ingredients, especially major allergens (e.g., nuts, dairy, gluten, soy, eggs), in your product descriptions.</li>
              <li><strong>Liability:</strong> SOHLocal does not inspect your kitchen or verify your compliance. You assume all liability for any illness, injury, or damages caused by the consumption of your products.</li>
            </ul>

            <h2>4. Buyer Obligations and Risks</h2>
            <p>
              If you purchase items through SOHLocal, you acknowledge and agree that:
            </p>
            <ul>
              <li><strong>Independent Verification:</strong> You are buying directly from an independent Seller, not from SOHLocal. You are responsible for verifying the Seller's credentials if necessary.</li>
              <li><strong>Allergies and Dietary Restrictions:</strong> You must communicate any severe allergies or dietary restrictions directly to the Seller prior to placing an order. Because food is prepared in shared home kitchens, cross-contamination is a risk.</li>
              <li><strong>Assumption of Risk:</strong> You consume products purchased through SOHLocal at your own risk.</li>
            </ul>

            <h2>5. Transactions, Payments, and Refunds</h2>
            <p>
              SOHLocal does not process payments for food orders. When an order is accepted, the Buyer and Seller must arrange payment directly (e.g., via Cash, e-Transfer, PayPal, Square, or other agreed-upon methods) upon pickup or delivery.
            </p>
            <ul>
              <li><strong>Cancellations:</strong> Buyers and Sellers must communicate cancellations directly. Sellers reserve the right to set their own cancellation and refund policies.</li>
              <li><strong>Disputes:</strong> Any disputes regarding payment, food quality, missing items, or refunds must be resolved directly between the Buyer and the Seller. SOHLocal is not responsible for mediating or resolving such disputes.</li>
            </ul>

            <h2>6. Prohibited Conduct</h2>
            <p>
              While using SOHLocal, you agree not to:
            </p>
            <ul>
              <li>Violate any local, state, or federal laws, including food safety and tax regulations.</li>
              <li>Post false, inaccurate, misleading, defamatory, or libelous content.</li>
              <li>Distribute viruses, malware, or any other technologies that may harm SOHLocal or its users.</li>
              <li>Harvest or collect personal information about users, including email addresses or phone numbers, without their consent.</li>
              <li>Use the platform for any illegal or unauthorized purpose.</li>
            </ul>

            <h2>7. Limitation of Liability</h2>
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 mb-6">
              <p className="text-sm font-bold m-0 text-slate-700 uppercase tracking-wide mb-2 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="mr-2 text-red-500" /> Important Disclaimer
              </p>
              <p className="text-sm text-slate-600 m-0 leading-relaxed uppercase tracking-wide">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, SOHLOCAL AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (A) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE PLATFORM; (B) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE PLATFORM, INCLUDING WITHOUT LIMITATION, ANY DEFAMATORY, OFFENSIVE, OR ILLEGAL CONDUCT OF OTHER USERS; OR (C) THE CONSUMPTION, USE, OR HANDLING OF ANY FOOD OR PRODUCTS PURCHASED THROUGH THE PLATFORM.
              </p>
            </div>

            <h2>8. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless SOHLocal and its affiliates, directors, officers, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including, without limitation, reasonable legal and accounting fees, arising out of or in any way connected with your access to or use of the platform, your violation of these Terms, or your sale or consumption of any products arranged through the platform.
            </p>

            <h2>9. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account and access to the platform at our sole discretion, without notice, for conduct that we believe violates these Terms and Conditions or is harmful to other users of the platform, us, or third parties, or for any other reason.
            </p>

            <h2>10. Modifications to Terms</h2>
            <p>
              SOHLocal reserves the right to modify these Terms and Conditions at any time. We will notify users of any material changes by posting the new Terms on the platform and updating the "Last Updated" date. Your continued use of the platform after any such changes constitutes your acceptance of the new Terms.
            </p>

            <h2>11. Contact Information</h2>
            <p>
              If you have any questions about these Terms and Conditions, please contact us via the Secure Contact page on our platform or at legal@sohlocal.com.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsPage;